import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Bot,
  Wallet,
  Shield,
  Settings,
  Users,
  Server,
  TrendingUp,
  BookOpen,
  FlaskConical,
  ScrollText,
  ClipboardList,
  Layers,
  Link2,
  Route,
  Puzzle,
  ChartCandlestick,
  Flag,
  ShieldAlert,
  Radio,
  Inbox,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { cn } from '@/shared/lib/utils';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { useKillSwitchStatus, useExchangeCBList } from '@/api/hooks/use-system';
import { SidebarFooter } from './SidebarFooter';

// ── Types ───────────────────────────────────────────────────────

interface NavItemDef {
  to: string;
  labelKey: string;
  icon: React.ElementType;
  requiresAdmin?: boolean;
  /** Minimum role: 'OPERATOR' hides for VIEWER */
  minRole?: 'OPERATOR' | 'ADMIN';
}

interface NavGroupDef {
  key: string;
  labelKey: string;
  items: NavItemDef[];
  /** Group is expanded by default on first visit */
  defaultExpanded: boolean;
  /** Pin to bottom of sidebar */
  anchor?: 'bottom';
  /** Show red accent + auto-expand when safety alert active */
  safetyAlert?: boolean;
}

// ── Collapse state store ────────────────────────────────────────

interface NavGroupState {
  expanded: Record<string, boolean>;
  toggle: (key: string) => void;
  isExpanded: (key: string, defaultExpanded: boolean) => boolean;
}

// Migrate old (LOK-1024) group keys to the new (LOK-1097) taxonomy on load.
// Runs once before zustand/persist hydrates so it operates on the raw blob.
function migrateLegacyNavGroupKeys() {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem('loki-nav-groups');
    if (!raw) return;
    const parsed = JSON.parse(raw) as { state?: { expanded?: Record<string, boolean> } };
    const expanded = parsed?.state?.expanded;
    if (!expanded || typeof expanded !== 'object') return;
    const migrated: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(expanded)) {
      if (key === 'core') migrated.operate = value;
      else if (key === 'configure') migrated.meta = value;
      else if (key === 'advanced') {
        // Old `advanced` items split between operate + research; drop the key
        // and let the new defaults take over.
        continue;
      } else migrated[key] = value;
    }
    parsed.state = { ...parsed.state, expanded: migrated };
    window.localStorage.setItem('loki-nav-groups', JSON.stringify(parsed));
  } catch {
    /* corrupt blob — let zustand re-init */
  }
}

migrateLegacyNavGroupKeys();

const useNavGroupState = create<NavGroupState>()(
  persist(
    (set, get) => ({
      expanded: {},
      toggle: (key: string) =>
        set((s) => ({
          expanded: { ...s.expanded, [key]: !get().expanded[key] },
        })),
      isExpanded: (key: string, defaultExpanded: boolean) => {
        const stored = get().expanded[key];
        return stored !== undefined ? stored : defaultExpanded;
      },
    }),
    { name: 'loki-nav-groups' },
  ),
);

// ── Group definitions (LOK-1097, spec docs/design/app-shell.md § Decision 1) ──

const NAV_GROUPS: NavGroupDef[] = [
  {
    key: 'operate',
    labelKey: 'nav.operate',
    defaultExpanded: true,
    items: [
      { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { to: '/bots', labelKey: 'nav.bots', icon: Bot },
      { to: '/execution-profiles', labelKey: 'nav.executionProfiles', icon: Route },
      { to: '/venue-connections', labelKey: 'nav.venueConnections', icon: Link2 },
      { to: '/strategies', labelKey: 'nav.strategies', icon: Puzzle },
      { to: '/wallets', labelKey: 'nav.wallets', icon: Wallet },
    ],
  },
  {
    key: 'monitor',
    labelKey: 'nav.monitor',
    defaultExpanded: false,
    items: [
      { to: '/portfolio', labelKey: 'nav.portfolio', icon: TrendingUp },
      { to: '/orders', labelKey: 'nav.orders', icon: ClipboardList },
      { to: '/positions', labelKey: 'nav.positions', icon: Layers },
      { to: '/alerts', labelKey: 'nav.alerts', icon: AlertCircle },
      { to: '/signals', labelKey: 'nav.signals', icon: Radio },
    ],
  },
  {
    key: 'safety',
    labelKey: 'nav.safety',
    defaultExpanded: false,
    safetyAlert: true,
    items: [
      { to: '/risk', labelKey: 'nav.riskSafety', icon: Shield },
      { to: '/safety', labelKey: 'nav.safetyControls', icon: ShieldAlert },
    ],
  },
  {
    key: 'research',
    labelKey: 'nav.research',
    defaultExpanded: false,
    items: [
      { to: '/backtests', labelKey: 'nav.backtests', icon: FlaskConical },
      { to: '/market-data', labelKey: 'nav.marketData', icon: ChartCandlestick },
      { to: '/glossary', labelKey: 'nav.glossary', icon: BookOpen },
    ],
  },
  {
    key: 'meta',
    labelKey: 'nav.meta',
    defaultExpanded: false,
    anchor: 'bottom',
    items: [
      { to: '/settings', labelKey: 'nav.settings', icon: Settings },
      { to: '/system', labelKey: 'nav.system', icon: Server },
    ],
  },
  {
    key: 'admin',
    labelKey: 'nav.admin',
    defaultExpanded: false,
    anchor: 'bottom',
    items: [
      { to: '/admin', labelKey: 'nav.admin', icon: Users, requiresAdmin: true },
      {
        to: '/admin/feature-flags',
        labelKey: 'nav.featureFlags',
        icon: Flag,
        requiresAdmin: true,
      },
      { to: '/admin/dlq', labelKey: 'nav.dlq', icon: Inbox, requiresAdmin: true },
      { to: '/admin/audit', labelKey: 'nav.audit', icon: ScrollText, requiresAdmin: true },
      {
        to: '/admin/security-events',
        labelKey: 'nav.securityEvents',
        icon: ShieldAlert,
        requiresAdmin: true,
      },
    ],
  },
];

// ── Component ───────────────────────────────────────────────────

interface SidebarProps {
  collapsed?: boolean;
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const { t } = useTranslation('common');
  const { canAdmin } = usePermissions();

  // Safety alert state for the "Safety" group red accent + auto-expand
  const { data: killSwitch } = useKillSwitchStatus();
  const { data: cbList } = useExchangeCBList();
  const hasSafetyAlert = !!(
    killSwitch?.active ||
    cbList?.data?.some((cb) => cb.status !== 'CLOSED' && cb.status !== 'closed')
  );

  const isVisible = useCallback(
    (item: NavItemDef) => {
      if (item.requiresAdmin && !canAdmin) return false;
      return true;
    },
    [canAdmin],
  );

  const mainGroups = NAV_GROUPS.filter((g) => !g.anchor);
  const bottomGroups = NAV_GROUPS.filter((g) => g.anchor === 'bottom');

  return (
    <aside
      className={cn(
        'flex flex-col h-full bg-sidebar border-r border-sidebar-border transition-all duration-200',
        collapsed ? 'w-14' : 'w-56',
      )}
    >
      {/* Brand row — height matches header for visual alignment */}
      <div className="flex items-center h-14 px-4 border-b border-sidebar-border shrink-0">
        <img src="/loki-icon.png" alt={t('appTitle')} className="h-7 w-7 shrink-0" />
        {!collapsed && (
          <span className="ml-2 font-semibold text-sidebar-foreground">{t('appTitle')}</span>
        )}
      </div>

      {/* Main nav */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {mainGroups.map((group) => (
          <CollapsibleNavGroup
            key={group.key}
            group={group}
            collapsed={collapsed}
            isVisible={isVisible}
            hasSafetyAlert={group.safetyAlert ? hasSafetyAlert : false}
          />
        ))}
      </nav>

      {/* Bottom nav (Meta + Admin) */}
      <div className="py-2 border-t border-sidebar-border shrink-0">
        {bottomGroups.map((group) => (
          <CollapsibleNavGroup
            key={group.key}
            group={group}
            collapsed={collapsed}
            isVisible={isVisible}
            hasSafetyAlert={false}
          />
        ))}
      </div>

      {/* User menu (relocated from header per LOK-1097 spec § Sidebar footer) */}
      <SidebarFooter collapsed={collapsed} />
    </aside>
  );
}

// ── Collapsible group ───────────────────────────────────────────

function CollapsibleNavGroup({
  group,
  collapsed,
  isVisible,
  hasSafetyAlert,
}: {
  group: NavGroupDef;
  collapsed: boolean;
  isVisible: (item: NavItemDef) => boolean;
  hasSafetyAlert: boolean;
}) {
  const { t } = useTranslation('common');
  const { isExpanded, toggle } = useNavGroupState();
  const persistedExpanded = isExpanded(group.key, group.defaultExpanded);
  // Safety auto-expand is transient — overrides the persisted state without
  // mutating it. When the alert clears the user keeps their original choice.
  const expanded = hasSafetyAlert ? true : persistedExpanded;

  const visibleItems = group.items.filter(isVisible);
  if (visibleItems.length === 0) return null;

  // When sidebar is collapsed: render items as a flat icon list with a thin
  // top divider that preserves implicit grouping without labels.
  if (collapsed) {
    return (
      <ul className="space-y-0.5 px-2 mb-1 border-t border-white/[0.04] first:border-t-0 pt-1 first:pt-0">
        {visibleItems.map((item) => (
          <li key={item.to}>
            <SidebarNavItem item={item} collapsed />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      className={cn(
        'mb-1',
        hasSafetyAlert && 'bg-red-500/5 border-l-2 border-red-500 rounded-r-sm',
      )}
    >
      {/* Group header (clickable to collapse/expand) */}
      <button
        type="button"
        onClick={() => toggle(group.key)}
        aria-expanded={expanded}
        className="flex items-center justify-between w-full px-4 py-1.5 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-wider hover:text-sidebar-foreground/60 transition-colors"
      >
        <span className={cn(hasSafetyAlert && 'text-red-500/70')}>
          {t(group.labelKey)}
          {hasSafetyAlert && ' ●'}
        </span>
        <ChevronDown
          className={cn('h-3 w-3 transition-transform duration-150', !expanded && '-rotate-90')}
        />
      </button>

      {/* Items (collapsible) */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <ul className="space-y-0.5 px-2 pb-1">
          {visibleItems.map((item) => (
            <li key={item.to}>
              <SidebarNavItem item={item} collapsed={false} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Nav item ────────────────────────────────────────────────────

function SidebarNavItem({ item, collapsed }: { item: NavItemDef; collapsed: boolean }) {
  const { t } = useTranslation('common');
  const label = t(item.labelKey);

  return (
    <NavLink
      to={item.to}
      end={item.to === '/admin'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors',
          'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
          isActive && 'text-sidebar-foreground bg-sidebar-accent font-medium',
          collapsed && 'justify-center px-2',
        )
      }
      title={collapsed ? label : undefined}
    >
      <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}
